import type { ReactNode } from "react";

/**
 * La carte blanche, recopiée de `designSystem/molecules/WhiteCard.tsx` du proto :
 *
 *     rounded-[var(--mg-radius-card)] bg-mg-surface shadow-[var(--mg-shadow-card)]
 *     p-sm (16) · p-md (24) · p-lg (32)
 *
 * ⚠️ Le proto ne met AUCUN trait autour de sa carte : elle se détache par son
 * ombre. On lui posait une bordure `mg-border` et pas d'ombre, ce qui donnait des
 * boîtes plates cernées de gris — c'est le premier écart qu'on voit en mettant les
 * deux écrans côte à côte.
 *
 * Le rayon est 8px (`--mg-radius-card`), pas 12 : ce token vient de `old.scss`,
 * que la V3.2 n'a pas redéfini.
 */

type Size = "medium" | "large";

/** `p-md` / `p-lg` de l'échelle du proto : 24, 32. (`p-sm`, 16, n'a aucun usage.) */
const PADDING: Record<Size, string> = {
	medium: "p-6",
	large: "p-8",
};

export function Card({
	children,
	className = "",
	size = "medium",
	padded = true,
}: {
	children: ReactNode;
	className?: string;
	size?: Size;
	/** `false` quand la carte porte elle-même son en-tête et ses rangées. */
	padded?: boolean;
}) {
	return (
		<section
			className={`flex h-fit flex-col overflow-hidden rounded-card bg-mg-surface shadow-card ${padded ? PADDING[size] : ""} ${className}`}
		>
			{children}
		</section>
	);
}

/**
 * L'en-tête d'une carte : titre, sous-titre, action à droite.
 *
 * Le proto n'a pas ce composant — sa `WhiteCard` prend un `title` en gras et rien
 * d'autre. Ce n'est donc pas une primitive recopiée mais une COMPOSITION, et elle
 * n'invente que sa mise en page : le filet est `mg-border-subtle`, les tailles
 * viennent de l'échelle, les espacements de `--mg-space-*`.
 */
export function CardHeader({
	icon,
	title,
	subtitle,
	action,
}: {
	/**
	 * La pastille d'icône du proto : carré 40px `rounded-md bg-mg-page`, icône 20px
	 * en `mg-icon-secondary`. C'est ce qui distingue une carte finie d'une boîte à
	 * texte — cinq cartes sans une seule icône se lisent comme un document, pas
	 * comme un écran.
	 */
	icon?: ReactNode;
	title: string;
	subtitle?: string;
	action?: ReactNode;
}) {
	return (
		<div className="flex flex-col items-start gap-3 border-b border-mg-border-subtle px-6 py-4 md:flex-row md:items-start md:justify-between md:gap-4">
			<div className="flex min-w-0 items-center gap-3 md:flex-1">
				{icon ? (
					<span className="grid size-10 shrink-0 place-items-center rounded-md bg-mg-page text-mg-icon-secondary [&>svg]:size-5">
						{icon}
					</span>
				) : null}
				<div className="min-w-0">
					<h2 className="t-h3 break-words text-mg-heading">{title}</h2>
					{/* 14/20 en `mg-secondary` : ni 12/16 en `mg-text-muted`, qui faisait
					    une note de bas de page illisible sous chaque titre, ni 16/24, qui
					    rendait la ligne méta (« Cabinet Ardoise · reçue hier ») plus grosse
					    que le contenu de la carte. C'est la taille du même sous-titre côté
					    entreprise. */}
					{subtitle ? <p className="t-body mt-0.5 text-mg-secondary">{subtitle}</p> : null}
				</div>
			</div>
			{/* Toujours sous le titre en 390, toujours en haut à droite en 1440 : un badge
			    qui saute d'une carte à l'autre selon la longueur du titre se lit comme un
			    désordre. */}
			{action ? <div className="md:shrink-0">{action}</div> : null}
		</div>
	);
}

/**
 * Le bloc encastré : le beige de page qui revient DANS la carte blanche.
 *
 * C'est le geste de relief le moins cher du proto, et il sert partout où une
 * valeur se LIT sans se modifier. Sans lui, une donnée en lecture seule est du
 * texte nu posé sur du blanc, et rien ne la distingue d'un champ vide.
 */
export function ReadOnlyBlock({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div>
			<span className="t-body mb-1 block text-mg-secondary">{label}</span>
			{/* `wrap-anywhere` : une adresse email sans tiret (une seule « ligne » de 70
			 caractères) débordait du cadre en 390 au lieu de passer à la ligne. */}
			<div className="t-body-lg rounded-md border border-mg-border bg-mg-page px-3 py-2.5 text-mg-body wrap-anywhere">
				{children}
			</div>
		</div>
	);
}
